import * as io from "io-ts";
import React from "react";
import closestValueIndex from "../../../lib/closestValueIndex";
import createGlobalState from "@ui/lib/createGlobalState";
import useDistinctShape from "@ui/lib/hooks/useDistinctShape";
import isFuncState from "../../../lib/isFuncState";
import { nullable } from "diagnostics-lib/io";
export interface Boundary {
  pivot: number | null; // first point clicked
  to: number | null; // second point (could be before the pivot)
}

interface Analysis {
  date: number;
}

export function closestSelectableAnalysis<T extends Analysis>(analyses: T[], date: number) {
  const analysisIndex = closestValueIndex(
    analyses.map(d => Number(d.date)),
    date,
  );
  if (analysisIndex === null) return null;
  const closestAnalysis = analyses[analysisIndex];
  if (!closestAnalysis) throw new Error("Missing closestAnalysis");
  return closestAnalysis;
}

export const useDateRange = createGlobalState({
  storage: "session",
  key: "selectedAnalysesRange",
  type: io.strict({
    pivot: nullable(io.number),
    to: nullable(io.number),
  }),
  defaultValue: { pivot: null, to: null },
  scoped: true,
});

function getAnalysisAt<T extends Analysis>(analyses: T[], date: number) {
  return analyses.find(a => a.date === date) ?? null;
}

function useSelectedAnalyses<Analysis extends { date: number }>(
  projectId: string,
  analyses: Analysis[],
) {
  const [_dateRange, setDateRange] = useDateRange({
    scope: projectId,
  });

  const dateRange = useDistinctShape(_dateRange);

  // contains all analyses in the selected range (between from & to inclusive)
  const selectedAnalyses = React.useMemo(() => {
    const previousAnalysis = analyses[analyses.length - 1];
    if (!previousAnalysis) return [];

    const { pivot, to } = dateRange;

    if (pivot === null) {
      setDateRange({ pivot: previousAnalysis.date, to: null });
      return [previousAnalysis];
    }
    const pivotAnalysis = getAnalysisAt(analyses, pivot);

    if (to === null) return pivotAnalysis ? [pivotAnalysis] : [];
    return analyses.filter(a =>
      to <= pivot ? a.date >= to && a.date <= pivot : a.date >= pivot && a.date <= to,
    );
  }, [analyses, dateRange, setDateRange]);

  const setBoundary = React.useCallback(
    (boundary: React.SetStateAction<Boundary>) => {
      setDateRange(oldBoundary => {
        const { pivot: from, to } = isFuncState(boundary) ? boundary(oldBoundary) : boundary;

        if (from === null) {
          return { pivot: from, to: null };
        }
        if (to === null) {
          return { pivot: from, to };
        }
        if (from === to) {
          return { pivot: from, to: null };
        }
        return { pivot: from, to };
      });
    },
    [setDateRange],
  );

  return React.useMemo(
    () => ({
      boundary: dateRange,
      setBoundary,
      selectedAnalyses,
    }),
    [dateRange, setBoundary, selectedAnalyses],
  );
}

export default useSelectedAnalyses;
export type SelectedAnalyses = ReturnType<typeof useSelectedAnalyses>["selectedAnalyses"];
